import React, { useCallback, useEffect, useRef, useState } from "react";
import Icon from "./Icon";
import products from "../data/products";
import "../styles/RotatingProductCards.css";

/*
 * Self-contained rotating (carousel) product card layout.
 * Auto-rotates on an interval, pauses on hover/touch, and supports
 * manual prev/next + dot navigation. Accepts its own product list via
 * props so it can be reused for a "preview" set on Home and the full
 * catalogue on the HandiCrafts page — no shared state with siblings.
 */
export default function RotatingProductCards({
  items = products,
  autoRotate = true,
  intervalMs = 3200,
  title,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef(null);
  const total = items.length;

  const goTo = useCallback(
    (index) => {
      setActiveIndex(((index % total) + total) % total);
    },
    [total]
  );

  const goNext = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
  const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

  useEffect(() => {
    if (!autoRotate) return undefined;
    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % total);
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [autoRotate, intervalMs, total]);

  const pause = () => clearInterval(timerRef.current);
  const resume = () => {
    if (!autoRotate) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % total);
    }, intervalMs);
  };

  const getPositionClass = (index) => {
    const diff = (index - activeIndex + total) % total;
    if (diff === 0) return "tp-rpc__card--active";
    if (diff === 1) return "tp-rpc__card--next";
    if (diff === total - 1) return "tp-rpc__card--prev";
    if (diff === 2) return "tp-rpc__card--next2";
    if (diff === total - 2) return "tp-rpc__card--prev2";
    return "tp-rpc__card--hidden";
  };

  return (
    <div className="tp-rpc" onMouseEnter={pause} onMouseLeave={resume}>
      {title && <h3 className="tp-rpc__title">{title}</h3>}

      <div className="tp-rpc__stage">
        <button className="tp-rpc__nav tp-rpc__nav--prev" onClick={goPrev} aria-label="Previous product">
          <Icon name="chevronLeft" size={22} />
        </button>

        <div className="tp-rpc__track">
          {items.map((product, index) => (
            <article key={product.id} className={`tp-rpc__card ${getPositionClass(index)}`}>
              <div className="tp-rpc__photo-wrap">
                <img src={product.image} alt={product.name} className="tp-rpc__photo" loading="lazy" />
              </div>
              <div className="tp-rpc__info">
                <span className="tp-rpc__category">{product.category}</span>
                <h4 className="tp-rpc__name">{product.name}</h4>
                <span className="tp-rpc__code">Code: {product.code}</span>
              </div>
            </article>
          ))}
        </div>

        <button className="tp-rpc__nav tp-rpc__nav--next" onClick={goNext} aria-label="Next product">
          <Icon name="chevronRight" size={22} />
        </button>
      </div>

      <div className="tp-rpc__dots" role="tablist" aria-label="Select product">
        {items.map((product, index) => (
          <button
            key={product.id}
            role="tab"
            aria-selected={index === activeIndex}
            aria-label={product.name}
            className={`tp-rpc__dot ${index === activeIndex ? "tp-rpc__dot--active" : ""}`}
            onClick={() => goTo(index)}
          />
        ))}
      </div>
    </div>
  );
}
